import { ReactElement, ReactNode } from "react";
import { Tooltip } from "@mui/material";
import {
  AddIcCall,
  Radar,
  SettingsSuggest,
  Troubleshoot,
} from "@mui/icons-material";
import Application, { Applications } from "../types/application";
import User from "../types/user";
import checkRole, { UserRoles } from "./checkUserRoles";
import getPermissions from "./getAppPermissions";
import getAdminAppRole from "./getAdminAppRole";
import CompanyLicense from "../types/companyLicense";

export interface AppRouteAction {
  icon: ReactNode;
  function: (id: number) => void;
  enabled: boolean;
}

export interface UserRowMetadata {
  user: User;
  canEdit: boolean;
  appRoutes: AppRouteAction[];
}

const getAppIcon = (id: number, enabled: boolean): ReactElement => {
  switch (id) {
    case Applications.Detector:
      return (
        <Radar
          fontSize="small"
          aria-label="detector"
          color={enabled ? "info" : "disabled"}
        />
      );
    case Applications.Gmd:
      return (
        <SettingsSuggest
          fontSize="small"
          aria-label="gmd"
          color={enabled ? "warning" : "disabled"}
        />
      );
    case Applications.Responder:
      return (
        <AddIcCall
          fontSize="small"
          aria-label="responder"
          color={enabled ? "primary" : "disabled"}
        />
      );
    case Applications.Investigator:
      return (
        <Troubleshoot
          fontSize="small"
          aria-label="investigator"
          color={enabled ? "success" : "disabled"}
        />
      );
    default:
      return <></>;
  }
};

const canUserEdit = (user: User, currentUser?: User): boolean => {
  if (!currentUser || !user?.roles?.length) return false;
  if (user.id === currentUser.id) return false;

  if (checkRole(currentUser, UserRoles.GlobalAdmin)) return true;

  return user.roles.some((role) => {
    const adminRole = getAdminAppRole(role.id as UserRoles);
    return !!adminRole && checkRole(currentUser, adminRole);
  });
};

const buildAppRoutes = (
  user: User,
  enabledApplications?: Application[],
  dbUser?: User,
  handleAppRoute?: (id: number, url: string) => void,
  licenses?: CompanyLicense[],
): AppRouteAction[] => {
  if (
    !enabledApplications?.length ||
    !dbUser ||
    !handleAppRoute ||
    !checkRole(dbUser, UserRoles.AnyAdmin)
  ) {
    return [];
  }

  return enabledApplications.map((app) => {
    let baseUrl = app.url;
    let enabled = false;

    switch (app.id) {
      case Applications.Detector:
        baseUrl += "/users";
        enabled =
          getPermissions(
            dbUser,
            UserRoles.DetectorAdmin,
            licenses?.find((x) => x.product.id === app.id),
          ) && checkRole(user, UserRoles.AnyDetector);
        break;
      case Applications.Responder:
        baseUrl += "/app/user-management";
        enabled =
          getPermissions(
            dbUser,
            UserRoles.ResponderAdmin,
            licenses?.find((x) => x.product.id === app.id),
          ) && checkRole(user, UserRoles.AnyResponder);
        break;
      case Applications.Gmd:
        baseUrl += "/";
        enabled =
          getPermissions(
            dbUser,
            UserRoles.GmdAdmin,
            licenses?.find((x) => x.product.id === app.id),
          ) && checkRole(user, UserRoles.AnyGmd);
        break;
      case Applications.Investigator:
        baseUrl += "/app/users";
        enabled =
          getPermissions(
            dbUser,
            UserRoles.InvestigatorAdmin,
            licenses?.find((x) => x.product.id === app.id),
          ) && checkRole(user, UserRoles.AnyInvestigator);
        break;
      default:
        break;
    }

    return {
      icon: <Tooltip title={app.name}>{getAppIcon(app.id, enabled)}</Tooltip>,
      function: (id: number) => handleAppRoute(id, baseUrl),
      enabled,
    };
  });
};

export const buildUserRowMetadata = (
  userList: User[],
  enabledApplications?: Application[],
  dbUser?: User,
  handleAppRoute?: (id: number, url: string) => void,
  licenses?: CompanyLicense[],
): UserRowMetadata[] => {
  if (!userList?.length) return [];

  return userList.map((user) => {
    const canEdit = canUserEdit(user, dbUser);
    const appRoutes = canEdit
      ? buildAppRoutes(
          user,
          enabledApplications,
          dbUser,
          handleAppRoute,
          licenses,
        )
      : [];

    return {
      user,
      canEdit,
      appRoutes,
    };
  });
};
